import type { AxiosInstance } from "axios";
import type {
  SchoolReportFilters,
  SchoolReportMetric,
  SchoolAcademicReport,
  SchoolReportRows,
  SchoolSetupBlock,
  SchoolAgendaBlock,
  SchoolPendingBlock,
} from "~/types/schoolReports";
import { createPublicRequestConfig } from "~/repository/authSession";
import HttpFactory from "~/repository/factory";
import { ENDPOINTS } from "~/repository/endpoints";
import type {
  OrganizationDomainRequest,
  OrganizationDomainRequestsResponse,
  OrganizationAssignableTeacherResponse,
  OrganizationBillingAccountResponse,
  OrganizationBillingAccountUpdateInput,
  OrganizationCommercialConfigResponse,
  OrganizationCommercialUsageResponse,
  OrganizationContextResponse,
  OrganizationInvitationCreateInput,
  OrganizationInvitationResponse,
  OrganizationMemberResponse,
  OrganizationMemberUpdateInput,
  OrganizationPreferencesResponse,
  OrganizationPreferencesUpdateInput,
  OrganizationPublicPreferencesResponse,
  OrganizationReportsOverviewResponse,
  OrganizationRole,
  OrganizationRolePolicyResponse,
  OrganizationRolePolicyUpdateInput,
  SwitchOrganizationContextInput,
  SwitchOrganizationContextResponse,
} from "~/types/organizations";

class OrganizationsModule extends HttpFactory {
  constructor(axiosInstance: AxiosInstance) {
    super(axiosInstance, "school");
  }

  getAcademicReport(
    filters: SchoolReportFilters,
  ): Promise<SchoolAcademicReport> {
    return this.get("/api/organizations/current/reports/academic/", {
      ...filters,
    });
  }

  getAcademicReportRows(
    filters: SchoolReportFilters,
    metric: SchoolReportMetric,
    page = 1,
  ): Promise<SchoolReportRows> {
    return this.get("/api/organizations/current/reports/academic/", {
      ...filters,
      metric,
      page,
    });
  }

  exportAcademicReport(
    filters: SchoolReportFilters,
    metric: SchoolReportMetric,
  ): Promise<Blob> {
    return this.get(
      "/api/organizations/current/reports/academic/",
      { ...filters, metric, export: "csv" },
      { responseType: "blob" },
    );
  }

  getDashboardSetup(): Promise<SchoolSetupBlock> {
    return this.get("/api/organizations/current/dashboard/", {
      block: "setup",
    });
  }

  getDashboardAgenda(): Promise<SchoolAgendaBlock> {
    return this.get("/api/organizations/current/dashboard/", {
      block: "agenda",
    });
  }

  getDashboardPending(): Promise<SchoolPendingBlock> {
    return this.get("/api/organizations/current/dashboard/", {
      block: "pending",
    });
  }

  private memberDetailUrl(memberId: number): string {
    return ENDPOINTS.ORGANIZATIONS.CURRENT_MEMBER_DETAIL.replace(
      "{memberId}",
      memberId.toString(),
    );
  }

  private invitationActionUrl(
    invitationId: number,
    action: "resend" | "cancel",
  ): string {
    const endpoint =
      action === "resend"
        ? ENDPOINTS.ORGANIZATIONS.CURRENT_INVITATION_RESEND
        : ENDPOINTS.ORGANIZATIONS.CURRENT_INVITATION_CANCEL;
    return endpoint.replace("{invitationId}", invitationId.toString());
  }

  private rolePolicyDetailUrl(role: OrganizationRole): string {
    return ENDPOINTS.ORGANIZATIONS.CURRENT_ROLE_POLICY_DETAIL.replace(
      "{role}",
      role,
    );
  }

  private publicPreferencesUrl(slug: string): string {
    return ENDPOINTS.ORGANIZATIONS.PUBLIC_PREFERENCES_BY_SLUG.replace(
      "{slug}",
      slug,
    );
  }

  async mine(): Promise<OrganizationContextResponse> {
    return await this.get(`${ENDPOINTS.ORGANIZATIONS.MINE}`);
  }

  async switchContext(
    data: SwitchOrganizationContextInput,
  ): Promise<SwitchOrganizationContextResponse> {
    return await this.post(`${ENDPOINTS.ORGANIZATIONS.SWITCH_CONTEXT}`, data);
  }

  async listMembers(): Promise<OrganizationMemberResponse[]> {
    return await this.get<OrganizationMemberResponse[]>(
      ENDPOINTS.ORGANIZATIONS.CURRENT_MEMBERS,
    );
  }

  async listAssignableTeachers(): Promise<
    OrganizationAssignableTeacherResponse[]
  > {
    return await this.get<OrganizationAssignableTeacherResponse[]>(
      ENDPOINTS.ORGANIZATIONS.CURRENT_ASSIGNABLE_TEACHERS,
    );
  }

  async getBillingAccount(): Promise<OrganizationBillingAccountResponse> {
    return await this.get<OrganizationBillingAccountResponse>(
      ENDPOINTS.ORGANIZATIONS.CURRENT_BILLING_ACCOUNT,
    );
  }

  async updateBillingAccount(
    data: OrganizationBillingAccountUpdateInput,
  ): Promise<OrganizationBillingAccountResponse> {
    return await this.patch<OrganizationBillingAccountResponse>(
      ENDPOINTS.ORGANIZATIONS.CURRENT_BILLING_ACCOUNT,
      data,
    );
  }

  async getCommercialConfig(): Promise<OrganizationCommercialConfigResponse> {
    return await this.get<OrganizationCommercialConfigResponse>(
      ENDPOINTS.ORGANIZATIONS.CURRENT_COMMERCIAL_CONFIG,
    );
  }

  async getCommercialUsage(): Promise<OrganizationCommercialUsageResponse> {
    return await this.get<OrganizationCommercialUsageResponse>(
      ENDPOINTS.ORGANIZATIONS.CURRENT_COMMERCIAL_USAGE,
    );
  }

  async getReportsOverview(): Promise<OrganizationReportsOverviewResponse> {
    return await this.get<OrganizationReportsOverviewResponse>(
      ENDPOINTS.ORGANIZATIONS.CURRENT_REPORTS_OVERVIEW,
    );
  }

  async getPreferences(): Promise<OrganizationPreferencesResponse> {
    return await this.get<OrganizationPreferencesResponse>(
      ENDPOINTS.ORGANIZATIONS.CURRENT_PREFERENCES,
    );
  }

  async updatePreferences(
    data: OrganizationPreferencesUpdateInput | FormData,
  ): Promise<OrganizationPreferencesResponse> {
    return await this.patch<OrganizationPreferencesResponse>(
      ENDPOINTS.ORGANIZATIONS.CURRENT_PREFERENCES,
      data,
    );
  }

  async verifyPreferencesDomain(): Promise<OrganizationPreferencesResponse> {
    return await this.post<OrganizationPreferencesResponse>(
      ENDPOINTS.ORGANIZATIONS.CURRENT_PREFERENCES_VERIFY_DOMAIN,
      {},
    );
  }

  async getPublicPreferencesBySlug(
    slug: string,
  ): Promise<OrganizationPublicPreferencesResponse> {
    return await this.get<OrganizationPublicPreferencesResponse>(
      this.publicPreferencesUrl(encodeURIComponent(slug)),
      {},
      createPublicRequestConfig(),
    );
  }

  async getPublicPreferencesByDomain(): Promise<OrganizationPublicPreferencesResponse> {
    return await this.get<OrganizationPublicPreferencesResponse>(
      ENDPOINTS.ORGANIZATIONS.DOMAIN_PREFERENCES,
      {},
      createPublicRequestConfig(),
    );
  }

  async getDomainRequests(
    purpose: "product" | "store" = "product",
  ): Promise<OrganizationDomainRequestsResponse> {
    return await this.get(
      ENDPOINTS.ORGANIZATIONS.CURRENT_DOMAIN_REQUESTS,
      purpose === "store" ? { purpose } : {},
    );
  }

  async requestDomain(
    domain: string,
    purpose: "product" | "store" = "product",
  ): Promise<OrganizationDomainRequest> {
    return await this.post(ENDPOINTS.ORGANIZATIONS.CURRENT_DOMAIN_REQUESTS, {
      domain,
      ...(purpose === "store" ? { purpose } : {}),
    });
  }

  async changeDomainRequest(
    id: number,
    action: "verify" | "cancel",
    expectedVersion: number,
  ): Promise<OrganizationDomainRequest> {
    return await this.post(
      `${ENDPOINTS.ORGANIZATIONS.CURRENT_DOMAIN_REQUESTS}${id}/${action}/`,
      { expected_version: expectedVersion },
    );
  }

  async updateMember(
    memberId: number,
    data: OrganizationMemberUpdateInput,
  ): Promise<OrganizationMemberResponse> {
    return await this.patch<OrganizationMemberResponse>(
      this.memberDetailUrl(memberId),
      data,
    );
  }

  async listInvitations(): Promise<OrganizationInvitationResponse[]> {
    return await this.get<OrganizationInvitationResponse[]>(
      ENDPOINTS.ORGANIZATIONS.CURRENT_INVITATIONS,
    );
  }

  async createInvitation(
    data: OrganizationInvitationCreateInput,
  ): Promise<OrganizationInvitationResponse> {
    return await this.post<OrganizationInvitationResponse>(
      ENDPOINTS.ORGANIZATIONS.CURRENT_INVITATIONS,
      data,
    );
  }

  async resendInvitation(
    invitationId: number,
  ): Promise<OrganizationInvitationResponse> {
    return await this.post<OrganizationInvitationResponse>(
      this.invitationActionUrl(invitationId, "resend"),
      {},
    );
  }

  async cancelInvitation(
    invitationId: number,
  ): Promise<OrganizationInvitationResponse> {
    return await this.post<OrganizationInvitationResponse>(
      this.invitationActionUrl(invitationId, "cancel"),
      {},
    );
  }

  async acceptInvitation(token: string): Promise<OrganizationMemberResponse> {
    return await this.post<OrganizationMemberResponse>(
      ENDPOINTS.ORGANIZATIONS.INVITATION_ACCEPT.replace("{token}", token),
      {},
    );
  }

  async listRolePolicies(): Promise<OrganizationRolePolicyResponse[]> {
    return await this.get<OrganizationRolePolicyResponse[]>(
      ENDPOINTS.ORGANIZATIONS.CURRENT_ROLE_POLICIES,
    );
  }

  createRolePolicy(
    data: OrganizationRolePolicyUpdateInput & { name: string },
  ): Promise<OrganizationRolePolicyResponse> {
    return this.post(ENDPOINTS.ORGANIZATIONS.CURRENT_ROLE_POLICIES, data);
  }

  deleteRolePolicy(role: OrganizationRole): Promise<void> {
    return this.delete(this.rolePolicyDetailUrl(role));
  }

  async updateRolePolicy(
    role: OrganizationRole,
    data: OrganizationRolePolicyUpdateInput,
  ): Promise<OrganizationRolePolicyResponse> {
    return await this.patch<OrganizationRolePolicyResponse>(
      this.rolePolicyDetailUrl(role),
      data,
    );
  }
}

export default OrganizationsModule;
